// Tiny Town Online client: fixed 60 Hz simulation for your own character, interpolated everyone else,
// and the town drawn with the original art.
import { C, INK, DUMMY_IMG, dummies } from '../legacy/art.gen.js';
import { sceneFor, type Scene } from './render/scene.js';
import { drawMob, drawHpBar, mobTop } from './render/mobs.js';
import { drawMount, drawLiftShadow, SEAT } from './render/mounts.js';
import { Maps, zoneOfName, type Dot, type Mark } from './ui/maps.js';
import { BattleScreen } from './ui/battle.js';
import { FormationUI } from './ui/formation.js';
import { zoneDef } from '../../shared/zones.js';
import { HIT, mobDef } from '../../shared/mobs.js';
import { FONT } from './render/ui2d.js';
import { INPUT_REDUNDANCY, INPUT_SEND_EVERY, SUB, TICK_MS } from '../../shared/constants.js';
import { FORAGE, INTERACT_RANGE_PX } from '../../shared/world.js';
import { factionById } from '../../shared/factions.js';
import { getMap } from '../../shared/map.js';
import type { Look } from '../../shared/look.js';
import type { ClientMsg, ServerMsg } from '../../shared/protocol.js';
import { animOf, Anim, Act, ATTACK_TICKS, DIR_NAMES, IN } from '../../shared/sim.js';
import { Follow, type FollowTarget } from './game/follow.js';
import { jobDef, type JobId } from '../../shared/jobs.js';
import { Connection } from './net/connection.js';
import { Predictor } from './game/prediction.js';
import { RemoteWorld, type Remote } from './game/world.js';
import { Input } from './game/input.js';
import { advance, makeNpcs, nearbyNpc, scheduleNpcs, startTalk, updateNpcs, walkNpcs, dirToward, type Dialogue } from './game/npcs.js';
import { Sky, type Light } from './render/sky.js';
import { buildRoom, drawWindows, type Room } from './render/rooms.js';
import type { Billboard, FirstPerson } from './render/fpv.js';
import { SnapCamera } from './ui/snap.js';
import { DOORS, DOOR_RANGE_PX, doorPoint, doorPush as pushOf, interiorDef } from '../../shared/interiors.js';
import { recentlyRained, type Weather } from '../../shared/time.js';
import { SpriteCache, attackFrame, type Mode } from './render/sprites.js';
import { drawBubble, drawDialogue, drawName } from './render/ui2d.js';
import { ChatBox, DebugPanel, setStatus, toast } from './ui/hud.js';
import { Gate, type CharacterSummary } from './ui/account.js';
import { Bag } from './ui/bag.js';
import { PartyUI } from './ui/party.js';
import { DevPanel } from './ui/dev.js';
import { CharacterUI } from './ui/character.js';
import { ModHost } from './mods/host.js';
import { BUILTIN_MODS } from './mods/examples.js';
import { modsTab } from './ui/mods.js';
import { itemDef, FOODS } from '../../shared/items.js';
import { levelForXp } from '../../shared/skills.js';
import { SettingsUI, type Settings } from './ui/settings.js';
import { AudioEngine, renderPreview } from './audio/audio.js';
import { itemIcon } from './render/items.js';

let map = getMap('town');
const CROWD_NAMES = 12;
const TS = map.ts;
let MW = map.w, MH = map.h;
let room: Room | null = null;                       // inside a building: its drawing
let scene: Scene | null = null;                     // outdoors: the zone's ground, props and lights
const rooms = new Map<string, Room>();
const npcsByMap = new Map<string, ReturnType<typeof makeNpcs>>();
const sprites = new SpriteCache();
const input = new Input();
const chat = new ChatBox();
const debug = new DebugPanel();
let npcs = makeNpcs(map);
npcsByMap.set('town', npcs);
/** Switch what we draw (and the walls prediction uses) to another zone's map. */
function setMap(id: string): void {
  if (map.id === id) return;
  map = getMap(id); MW = map.w; MH = map.h;
  pred?.setMap(map);
  const r = interiorDef(id);
  room = r ? (rooms.get(id) ?? (rooms.set(id, buildRoom(r)), rooms.get(id)!)) : null;
  scene = room ? null : sceneFor(id, map.data.collision, MW, MH);
  document.getElementById('rideBtn')?.classList.toggle('off', !map.mounts);
  mobFx.clear(); floaters.length = 0;
  npcs = npcsByMap.get(id) ?? (npcsByMap.set(id, makeNpcs(map)), npcsByMap.get(id)!);
  scheduleNpcs(npcs, sky.hour(), 0, true);
  dlg = null;
}
scene = sceneFor('town', map.data.collision, MW, MH);
const sky = new Sky();
const clockEl = document.getElementById('clock')!;
// ---------- creatures and fighting: what the server tells us, plus floating numbers ----------
const mobFx = new Map<number, { hurtAt: number; deadAt: number }>();
const floaters: { x: number; y: number; text: string; color: string; big: boolean; t0: number }[] = [];
let myFood: { id: string; until: number } | null = null;
/** The meal buff on the minimap: what it is and how long is left. */
function showFood(): void {
  const tag = document.getElementById('foodTag')!, f = myFood ? FOODS[myFood.id] : null;
  const left = myFood ? Math.ceil((myFood.until - Date.now()) / 60000) : 0;
  tag.hidden = !f || left <= 0 || !pred; if (!f || left <= 0) return;
  const text = `${f.icon} ${f.buff} · ${left}m`; if (tag.textContent !== text) { tag.textContent = text; tag.title = f.desc; }
}
setInterval(showFood, 15000);
let myHp = 60, myMax = 60, myHurtAt = 0, myMp = 0, myMaxMp = 0, myJob: JobId = 'knight', myJobLv = 1, zoneNow = '';
const maps = new Maps();
const hpBar = document.getElementById('hpBar')!;
function showHp(): void {
  const field = zoneDef(map.id.split('~')[0])?.kind === 'field';
  hpBar.hidden = !(field || myHp < myMax);
  const k = myHp / Math.max(1, myMax);
  (hpBar.firstElementChild as HTMLElement).style.width = `${Math.round(k * 100)}%`;
  hpBar.querySelector('em')!.textContent = `♥ ${myHp} / ${myMax}`;
  hpBar.classList.toggle('mid', k <= 0.5 && k > 0.25); hpBar.classList.toggle('low', k <= 0.25);
  const mpBar = document.getElementById('mpBar')!;
  mpBar.hidden = hpBar.hidden || !myMaxMp;
  (mpBar.firstElementChild as HTMLElement).style.width = `${Math.round(myMp / Math.max(1, myMaxMp) * 100)}%`;
  mpBar.querySelector('em')!.textContent = `✦ ${myMp} / ${myMaxMp}`;
  const tag = document.getElementById('jobTag')!, jd = jobDef(myJob);
  tag.hidden = !pred; tag.textContent = `${jd.icon} ${jd.name} ${myJobLv}`; tag.style.background = jd.color;
}
/** Is this forage spot there right now? Night spots open after dark, rain spots after a shower. */
const nodeOpen = (n: typeof FORAGE[number]) => !n.when || (n.when === 'night' ? sky.night() : recentlyRained(n.map, sky.now(), sky.override));
const bag = new Bag();
const party = new PartyUI();
const follow = new Follow();
const followPill = document.getElementById('followPill')!;
follow.onChange = (name, why) => {
  followPill.hidden = !name;
  if (name) { followPill.replaceChildren(`Following ${name}`); const s = document.createElement('small'); s.textContent = 'move to stop'; followPill.append(s); chat.system(`You're following ${name}. Move to stop.`); }
  else chat.system(why === 'lost' ? 'You lost sight of who you were following.' : 'You stopped following.');
};
followPill.addEventListener('click', () => follow.stop());
party.following = () => follow.name;
party.onFollow = name => startFollow(name);
/** Follow someone by name ('' = your party leader). They must be someone you can see. */
function startFollow(name: string | null): void {
  if (name === null) return follow.stop();
  const who = name || party.leaderName();
  if (!who) { chat.system('Follow whom? Try /follow name, or join a party.'); return; }
  const r = [...world.remotes.values()].find(x => x.info?.kind === 'player' && x.info.name.toLowerCase() === who.toLowerCase() && !x.goneAt);
  if (!r) { chat.system(`${who} isn't nearby.`); return; }
  follow.start(r.info!.name);
}
function followTarget(): FollowTarget | null {
  if (!follow.name) return null;
  for (const r of world.remotes.values()) {
    if (r.goneAt || r.info?.kind !== 'player' || r.info.name !== follow.name) continue;
    const p = world.pose(r); if (!p) return null;
    return { x: p.x, y: p.y, mounted: !!p.m };
  }
  return null;
}
const dev = new DevPanel();
const charUI = new CharacterUI();
const mods = new ModHost(BUILTIN_MODS);
mods.print = (name, text) => chat.line('system', null, `[${name}] ${text}`);
const settings = new SettingsUI();
const audio = new AudioEngine();
sky.onThunder = () => audio.thunder();

// ---------- the sightseeing camera ----------
let groundFor = '', signImg: HTMLCanvasElement | null = null;
const FACE: [number, number][] = [[0, 1], [1, 0], [0, -1], [-1, 0]];
/** Which way a sprite should face to look right from the camera: towards you, away, or to a side. */
function facingFor(dir: number, cam: FirstPerson['cam']): number {
  const fx = Math.cos(cam.yaw), fy = Math.sin(cam.yaw), [px, py] = FACE[dir] ?? FACE[0], d = px * fx + py * fy;
  if (d < -0.6) return 0; if (d > 0.6) return 2;
  return px * -fy + py * fx > 0 ? 1 : 3;
}
const snap = new SnapCamera({
  start: () => {
    if (!pred) return null;
    if (room) { toast('The camera works outdoors for now.', 'info'); return null; }
    if (!scene) return null;
    if (groundFor !== scene.id) { groundFor = scene.id; snap.fp.setGround(scene.flat()); }
    if (!signImg) { signImg = document.createElement('canvas'); signImg.width = 24; signImg.height = 28; drawCottageSign(12, 27, signImg.getContext('2d')!); }
    return { x: pred.state.x / SUB, y: pred.state.y / SUB, dir: pred.state.dir, place: zoneName };
  },
  billboards: fp => {
    const now = performance.now(), out: Billboard[] = [];
    for (const pr of scene?.props ?? []) out.push({ x: pr.x, y: pr.y, img: pr.img, ax: pr.ox, kind: 'prop' });
    if (map.id === 'town') {
      for (const d of dummies as { x: number; y: number }[]) out.push({ x: d.x, y: d.y, img: DUMMY_IMG, ax: 9, kind: 'prop' });
      if (signImg) out.push({ x: 38 * TS + 8, y: 14 * TS + 14, img: signImg, ax: 12, kind: 'prop' });
    }
    for (const n of FORAGE) if (n.map === map.id && nodeOpen(n) && (nodeReady.get(n.id) ?? 0) <= now) out.push({ x: n.tile[0] * TS + 8, y: n.tile[1] * TS + 12, img: itemIcon(n.item), kind: 'prop' });
    for (const n of npcs) if (n.alpha > 0.05) {
      const s = charFrame(-1000 - npcs.indexOf(n), n.look, facingFor(['down', 'right', 'up', 'left'].indexOf(n.dir), fp.cam), Anim.Idle, 0, now);
      if (s) out.push({ x: n.x, y: n.y, img: s, ax: 16, alpha: n.alpha, kind: 'person', name: n.name });
    }
    for (const r of world.remotes.values()) {
      const ps = world.pose(r); if (!ps || !r.info || r.info.kind === 'mob') continue;
      const s = charFrame(r.id, r.info.look, facingFor(ps.dir, fp.cam), ps.anim, ps.t, now);
      if (s) out.push({ x: ps.x, y: ps.y, img: s, ax: 16, kind: 'person', name: r.info.name });
    }
    return out;
  },
  view: () => {
    const lights = (scene?.lamps ?? []).map(L => ({ x: L.x, y: L.y + 29, r: L.r }));
    return { hour: sky.hour(), ambient: sky.ambient(), darkness: sky.darkness(), rain: Math.min(1, sky.amount('rain') + sky.amount('storm')), fog: sky.amount('fog'),
      cloudy: Math.min(1, sky.amount('cloudy') + sky.amount('rain') + sky.amount('storm')), t: performance.now() / 1000, lights };
  },
  timeLabel: () => sky.label().replace(/^\S+ /, ''),
  shutter: () => audio.play('shutter'),
  photographed: names => conn?.send({ type: 'photo', names }),
});
snap.onToggle = on => { input.clear(); audio.play(on ? 'open' : 'close'); document.body.classList.toggle('snapping', on); };

// ---------- settings ----------
function applySettings(st: Settings): void {
  audio.setVolumes({ master: st.master, music: st.music, sfx: st.sfx, ui: st.ui, others: st.others });
  audio.playMusic(st.musicOn && !!pred);
  const root = document.documentElement.style;
  root.setProperty('--ui-scale', String(st.uiScale)); root.setProperty('--pad-scale', String(st.padSize));
  root.setProperty('--chat-size', String(st.chatSize)); root.setProperty('--chat-lines', String(st.chatLines)); root.setProperty('--chat-bg', String(st.chatBg));
  document.documentElement.dataset.theme = st.theme;
  document.querySelector('.pad')?.classList.toggle('lefty', st.leftHanded);
  chat.hidden = new Set(st.chatHide);
  debug.show(st.stats);
  resize();
}
settings.onChange = applySettings;
settings.tabs.push(modsTab(mods, () => settings.toggle(true)));
debug.onToggle = on => settings.set('stats', on);
// a soft click for every button in the interface
addEventListener('click', e => { if ((e.target as HTMLElement).closest?.('button')) audio.play('click'); }, true);
chat.onLine = (ch, mine) => { if (mine) return; if (ch === 'whisper') audio.play('whisper'); else if (ch === 'party' || ch === 'alliance') audio.play('ping'); };
const nodeReady = new Map<string, number>();   // forage spot -> time it refills for me (ms)
const newReq = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

let conn: Connection | null = null;
let pred: Predictor | null = null;
let me = { id: 0, name: '', faction: 1, look: null as Look | null };
const world = new RemoteWorld(() => me.id);
const bubbles = new Map<number, { text: string; from: number; until: number }>();
let dlg: Dialogue | null = null;
let inputHist: number[] = [];
let layer = 1, zoneName = '';
let acc = 0;

// ---------- canvas ----------
const cv = document.getElementById('world') as HTMLCanvasElement, ctx = cv.getContext('2d')!;
let S = 3, VW = 320, VH = 200;
/** Game pixels at the bottom of the screen hidden under the touch pad; the dialogue box sits above them. */
let padInset = 0;
function resize(): void {
  // the canvas's own size, not innerHeight: on phones the browser bars make 100vh taller than what you can see
  const cssW = cv.clientWidth || innerWidth, cssH = cv.clientHeight || innerHeight;
  const dpr = window.devicePixelRatio || 1, w = Math.round(cssW * dpr), h = Math.round(cssH * dpr);
  S = Math.max(2, Math.round(Math.min(w, h) / 300));   // ~300 game pixels across the shorter side
  VW = Math.ceil(w / S); VH = Math.ceil(h / S);
  cv.width = VW * S; cv.height = VH * S;
  const pad = document.querySelector<HTMLElement>('.pad');
  let top = cssH;
  if (pad && getComputedStyle(pad).display !== 'none') for (const c of pad.querySelectorAll<HTMLElement>('.dpad, .btns')) top = Math.min(top, c.getBoundingClientRect().top);
  const covered = Math.max(0, cssH - top);
  document.documentElement.style.setProperty('--pad-h', covered + 'px');
  padInset = Math.ceil(covered * dpr / S);
}
addEventListener('resize', resize); visualViewport?.addEventListener('resize', resize); addEventListener('orientationchange', () => setTimeout(resize, 200)); resize();

// ---------- sign in and characters ----------
function drawSprite(canvas: HTMLCanvasElement, look: Look): void {
  const s = sprites.get(look, 'down', 'i', 0, false, null, true), c = canvas.getContext('2d')!;
  c.clearRect(0, 0, canvas.width, canvas.height); if (s) c.drawImage(s, 0, 0);
}
const gate = new Gate(drawSprite);
const hudBtns = document.getElementById('hudBtns')!;
gate.onPlay = play;
void gate.start();

function play(c: CharacterSummary): void {
  conn = new Connection({ character: c.id, quiet: () => settings.s.quiet });
  me = { id: 0, name: c.name, faction: c.faction, look: c.look };
  conn.onMessage = onMessage;
  conn.onStatus = (s, why) => {
    if (s === 'online') { setStatus(''); gate.hideQueue(); }
    else if (s === 'reconnecting') setStatus(conn?.everOnline ? 'Reconnecting…' : 'Connecting…');
    else if (s === 'stopped') { setStatus(''); leaveWorld(); void gate.showChars(why || 'Disconnected.'); }
  };
  conn.start();
}

// ---------- turn-based battles ----------
type Circle = Extract<ServerMsg, { type: 'circles' }>['list'][number];
let circles: Circle[] = [];
let playerScreen = { x: 0, y: 0 };   // where you are on the canvas (pixels), for the battle intro
const battle = new BattleScreen({
  me: () => me.id,
  look: id => id === me.id ? me.look : (world.remotes.get(id)?.info?.look ?? world.infos.get(id)?.look ?? null),
  sprite: (key, look, dir, mode, fi) => sprites.get(look, dir, mode, fi, false, null, true),
  backdrop: (x, y) => {
    if (!scene) return null;
    // a dry patch near the fight: the battlefield shouldn't be half river
    const tx0 = Math.floor(x / TS), ty0 = Math.floor(y / TS), wet = (cx: number, cy: number) => { for (let dy = -3; dy <= 3; dy++) for (let dx = -5; dx <= 5; dx++) { const t = scene!.tiles[(cy + dy) * scene!.w + cx + dx]; if (t === undefined || t === 3) return true; } return false; };
    for (let r = 0; r < 14; r++) for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
      const cx = tx0 + dx, cy = ty0 + dy; if (cx < 6 || cy < 4 || cx >= scene.w - 6 || cy >= scene.h - 4) continue;
      if (!wet(cx, cy)) return { cv: scene.ground, x: cx * TS + 8, y: cy * TS + 8 };
    }
    return { cv: scene.ground, x, y };
  },
  night: () => sky.darkness(),
  items: () => {
    const n = new Map<string, number>();
    for (const s of bag.slots.values()) if (itemDef(s.def)?.restore) n.set(s.def, (n.get(s.def) ?? 0) + s.qty);
    return [...n].map(([def, qty]) => ({ def, qty }));
  },
  send: (cmd, target, skill) => conn?.send({ type: 'battle', cmd: cmd as never, target, skill }),
  sound: (name, pitch) => audio.play(name, undefined, pitch),
  snapshot: () => { const c = document.createElement('canvas'); c.width = cv.width; c.height = cv.height; c.getContext('2d')!.drawImage(cv, 0, 0); return { cv: c, ...playerScreen }; },
});
battle.onClose = () => { input.clear(); if (pred) pred.frozen = false; };
// the formation picker: leaders and solo players pick; everyone sees it
const formation = new FormationUI();
let companions = 0;
formation.onPick = id => conn?.send({ type: 'formation', id });
function syncFormation(): void {
  const p = party.party, inParty = !!p && p.members.length > 1;
  formation.set(formation.id, inParty ? p!.members.length : 1 + companions, !inParty || party.isLeader);
}
function setCircles(list: Circle[]): void { circles = list; if (pred) pred.circles = list.filter(c => !c.can && !c.mine); }

function leaveWorld(): void {
  follow.stop();
  conn?.leave(); conn = null; pred = null; me.id = 0; dlg = null;
  world.reset(); bag.toggle(false); party.reset(); charUI.reset(); mods.stopAll(); snap.toggle(false); dev.setEnabled(false); audio.playMusic(false); hudBtns.hidden = true; nodeReady.clear(); maps.setPlaying(false); battle.close(); setCircles([]); formation.setPlaying(false);
}
document.getElementById('queueLeave')!.addEventListener('click', () => { conn?.send({ type: 'unqueue' }); leaveWorld(); gate.hideQueue(); void gate.showChars(); });
document.getElementById('chatBtn')!.addEventListener('click', () => input.onChatKey(false));
document.getElementById('charsBtn')!.addEventListener('click', () => { leaveWorld(); void gate.showChars(); });
bag.onOp = op => conn?.send({ type: 'inv_op', req: newReq(), ...op } as ClientMsg);
charUI.onUnequip = slot => conn?.send({ type: 'inv_op', req: newReq(), op: 'unequip', slot });
charUI.onNotes = text => conn?.send({ type: 'notes', text });
charUI.onJob = id => conn?.send({ type: 'job', id });
charUI.canChangeJob = () => zoneNow === 'guild' || zoneNow === `home:${party.myChar}`;
charUI.drawSprite = (cv, look, dir) => { const s = sprites.get(look, dir, 'i', 0, false, null, true), c = cv.getContext('2d')!; c.clearRect(0, 0, cv.width, cv.height); if (s) c.drawImage(s, 0, 0); };
party.onOp = op => conn?.send({ type: 'party', ...op });
party.onFriend = op => conn?.send({ type: 'friend', ...op } as ClientMsg);
dev.onSim = sim => conn?.send({ type: 'dev', op: 'sim', sim });
dev.onStats = () => conn?.send({ type: 'dev', op: 'stats' });
dev.onGo = zone => conn?.send({ type: 'dev', op: 'go', zone });
dev.onCompanions = count => conn?.send({ type: 'dev', op: 'companions', count });
dev.onSky = (hour, weather) => conn?.send({ type: 'dev', op: 'sky', hour, weather: weather as Weather | null });
dev.client = () => ({ fps: Math.round(fps), worstFrameMs: Math.round(worstFrame), onScreen: world.remotes.size, corrections: pred?.corrections ?? 0, snaps: pred?.snaps ?? 0, ping: Math.round(conn?.rtt ?? 0), sounds: audio.playing });
party.onWhisper = name => { if (pred) { input.clear(); chat.openBox(false, `/w ${name} `); } };
chat.onCommand = c => {
  if (c.cmd === 'friend') return party.onFriend({ op: 'add', name: c.name });
  if (c.cmd === 'invite' || c.cmd === 'ainvite') return party.onOp({ op: c.cmd, name: c.name });
  if (c.cmd === 'kick' || c.cmd === 'promote') {
    const char = party.memberByName(c.name);
    return char ? party.onOp({ op: c.cmd, char }) : chat.system(`${c.name} isn't in your party.`);
  }
  if (c.cmd === 'seek') return party.onOp({ op: 'seek', on: c.on, note: c.note });
  if (c.cmd === 'finder') return party.toggleFinder(true);
  if (c.cmd === 'follow') return startFollow(c.name);
  if (c.cmd === 'unfollow') return follow.stop();
  party.onOp({ op: c.cmd });
};

function onMessage(m: ServerMsg): void {
  switch (m.type) {
    case 'welcome':
      zoneNow = m.zone.id; layer = m.zone.layer; zoneName = m.zone.name; party.myLayer = layer;
      sky.setServerTime(m.now); sky.override = m.sky; sky.zone = m.zone.map; scheduleNpcs(npcs, sky.hour(), 0, true);
      me.id = m.you; me.name = m.name; me.faction = m.faction; me.look = m.look; charUI.name = m.name; charUI.setLook(m.look); chat.myName = m.name; party.myChar = m.character; dev.setEnabled(!!m.dev);
      setMap(m.zone.map); maps.setPlaying(true); formation.setPlaying(true); maps.setZone(m.zone.id, m.zone.name, m.zone.layer); showHp();
      if (pred) pred.resetTo(m.state, m.seq); else pred = new Predictor(m.state, m.seq, map);
      inputHist = []; acc = 0; world.reset(); gate.hide(); hudBtns.hidden = false; resize();   // the touch pad shows again
      chat.system(m.resumed ? 'Reconnected.' : `Welcome to ${m.zone.name}${m.zone.layer > 1 ? ` (layer ${m.zone.layer})` : ''}, ${m.name}.`);
      audio.playMusic(settings.s.musicOn);
      mods.start();
      mods.emit('ready', { name: m.name, faction: m.faction, zone: m.zone.name, layer: m.zone.layer });
      break;
    case 'info': world.addInfo(m.ents); for (const i of m.ents) if (i.id === me.id && i.look) { me.look = i.look; charUI.setLook(i.look); } break;
    case 'snap': world.applySnapshot(m); pred?.reconcile(m.ack, m.you); break;
    case 'chat':
      chat.fromServer(m); mods.emit('chat', { ch: m.ch, name: m.name, text: m.text, to: m.to ?? null });
      if (m.ch === 'say') bubbles.set(m.from, { text: m.text, from: performance.now(), until: performance.now() + 4000 });
      break;
    case 'error': chat.system(m.msg); audio.play('error'); break;
    case 'inv': bag.setAll(m.slots, m.gold); charUI.setEquip(m.equip); emitInventory(); break;
    case 'inv_update': bag.apply(m.set, m.clear, m.gold); emitInventory(); if (m.eq) { charUI.applyEquip(m.eq); audio.play(m.eq.set.length ? 'open' : 'close'); } break;
    case 'quests': charUI.setQuests(m.list); mods.emit('quests', [...charUI.quests.values()]); break;
    case 'quest_update': charUI.updateQuest(m.quest); mods.emit('quests', [...charUI.quests.values()]); break;
    case 'journal': charUI.setJournal(m.entries, m.notes); break;
    case 'journal_add': charUI.addJournal(m.entry); break;
    case 'req_fail': toast(m.msg, 'err'); audio.play('error'); break;
    case 'skills': bag.setXp(m.xp); charUI.setXp(m.xp); emitSkills(); break;
    case 'skill_xp': bag.addXp(m.skill, m.xp); charUI.addXp(m.skill, m.xp); emitSkills(); break;
    case 'notice': mods.emit('notice', { kind: m.kind, text: m.text }); toast(m.text, m.kind); if (m.kind === 'loot') audio.play('loot'); else if (m.kind === 'level') audio.play('level'); if (m.kind !== 'loot') chat.system(m.text); break;
    case 'nodes': { const now = performance.now(); for (const [id, secs] of m.cooling) nodeReady.set(id, now + secs * 1000); break; }
    case 'pong': sky.setServerTime(m.now, conn?.rtt ?? 0); break;
    case 'sky': sky.override = m.sky; chat.system(m.sky ? 'A developer changed the sky.' : 'The sky is back to normal.'); break;
    case 'queue': gate.showQueue(m.position, m.size, m.etaSec); setStatus(''); break;
    case 'party_state': { const had = !!party.party; party.setState(m); syncFormation(); mods.emit('party', { party: m.party, alliance: m.alliance, seeking: m.seeking }); if (!had && m.party && m.party.members.length > 1) audio.play('join'); break; }
    case 'party_offer': party.offer(m); audio.play('invite'); break;
    case 'party_offer_gone': party.offerGone(m.offer); break;
    case 'finder': party.setFinder(m); break;
    case 'dev_state': dev.set(m); break;
    case 'friends': party.setFriends(m.list); break;
    case 'friend_update': { const was = party.friends.get(m.friend.char); party.friendUpdate(m.friend); if (was && was.online !== m.friend.online) chat.system(`${m.friend.name} is ${m.friend.online ? 'online' : 'offline'}.`); break; }
    case 'friend_gone': party.friendGone(m.char); break;
    case 'friend_offer': party.friendOffer(m); audio.play('invite'); break;
    case 'hp': myHp = m.hp; myMax = m.max; if (m.maxMp != null) { myMp = m.mp ?? 0; myMaxMp = m.maxMp; } showHp(); break;
    case 'job': myJob = m.id; myJobLv = m.level; charUI.setJob(m.id); showHp(); break;
    case 'food': myFood = m.id ? { id: m.id, until: m.until } : null; showFood(); break;
    case 'battle': { input.clear(); if (pred) pred.frozen = true; dlg = null; battle.update(m); const mine = m.battle.fighters.find(f => f.id === me.id); if (mine) { myHp = mine.hp; myMax = mine.max; if (mine.maxMp != null) { myMp = mine.mp ?? 0; myMaxMp = mine.maxMp; } } break; }
    case 'battle_end': battle.end(m); if (pred) pred.frozen = false; break;
    case 'circles': setCircles(m.list); break;
    case 'formation': formation.id = m.id; companions = m.companions; syncFormation(); break;
    case 'hits': onHits(m.list); break;
    case 'zone': zoneNow = m.zone.id; follow.newZone(); charUI.setJob(myJob); setCircles([]); doorWait = performance.now() + 400; doorPush = 0; setMap(m.zone.map); maps.setZone(m.zone.id, m.zone.name, m.zone.layer); showHp(); sky.zone = m.zone.map; if (pred) pred.resetTo(m.state, pred.seq); world.reset(); audio.play('open'); layer = m.zone.layer; party.myLayer = layer; chat.system(m.zone.name === zoneName ? `Now on layer ${m.zone.layer}.` : `Entered ${m.zone.name}.`); zoneName = m.zone.name; break;
  }
}

/** Hits and heals the server saw this tick. */
function onHits(list: [number, number, number, number, number][]): void {
  const now = performance.now();
  for (const [target, amt, hp, src, flags] of list) {
    if (target === me.id) {
      const p = pred ? { x: pred.state.x / SUB, y: pred.state.y / SUB } : { x: 0, y: 0 };
      if (flags & HIT.heal) { if (amt >= 10 && hp < myMax) floaters.push({ x: p.x, y: p.y - 30, text: `+${amt}`, color: '#8fe08a', big: false, t0: now }); }
      else if (flags & HIT.dodge) { floaters.push({ x: p.x, y: p.y - 30, text: 'dodge!', color: '#a3def4', big: false, t0: now }); audio.play('dodge'); }
      else { floaters.push({ x: p.x, y: p.y - 30, text: String(amt), color: '#ec5448', big: false, t0: now }); myHurtAt = now; audio.play('hurt'); hpBar.classList.remove('hurt'); void hpBar.offsetWidth; hpBar.classList.add('hurt'); }
      myHp = hp; showHp();
      continue;
    }
    const r = world.remotes.get(target), ps = r ? world.pose(r) : null;
    if (r?.info?.kind === 'mob') r.info = { ...r.info, hp };
    const fx = mobFx.get(target) ?? { hurtAt: 0, deadAt: 0 }; mobFx.set(target, fx);
    if (flags & HIT.heal) { fx.deadAt = 0; continue; }
    if (flags & HIT.dodge) continue;
    fx.hurtAt = now;
    if (ps) floaters.push({ x: ps.x + (Math.random() - 0.5) * 6, y: ps.y - mobTop(r?.info?.mob ?? 'puff') - 4, text: (flags & HIT.crit) ? `${amt}!` : String(amt), color: src === me.id ? ((flags & HIT.crit) ? '#ffe9a8' : '#ffffff') : '#e6dcc4', big: !!(flags & HIT.crit), t0: now });
    if (flags & HIT.kill) { fx.deadAt = now; audio.play('poof', ps && pred ? { dx: ps.x - pred.state.x / SUB, dy: ps.y - pred.state.y / SUB } : undefined, src === me.id ? 1 : 0.9); }
    else if (src === me.id) audio.play('hit', undefined, (flags & HIT.crit) ? 1.25 : 1);
  }
}

// ---------- the maps ----------
maps.scene = id => { const m = getMap(id); return sceneFor(id, m.data.collision, m.w, m.h); };
maps.people = () => {
  const party_ = new Map<string, string[]>(), friends = new Map<string, string[]>();
  const add = (to: Map<string, string[]>, zone: string, name: string) => { const z = zoneOfName(zone); if (z) (to.get(z) ?? (to.set(z, []), to.get(z)!)).push(name); };
  for (const m of party.party?.members ?? []) if (m.online && m.char !== party.myChar) add(party_, m.zone, m.name);
  for (const f of party.friends.values()) if (f.online) add(friends, f.zone, f.name);
  return { party: party_, friends };
};
maps.onOpen = open => { input.clear(); audio.play(open ? 'open' : 'close'); };
let miniT = 0;
function drawMinimap(now: number): void {
  if (now - miniT < 120 || !pred) return; miniT = now;
  const dots: Dot[] = [], marks: Mark[] = [], mates = party.memberNames();
  for (const r of world.remotes.values()) {
    const ps = world.pose(r); if (!ps || !r.info) continue;
    if (r.info.kind === 'mob') { if ((r.info.hp ?? 1) > 0) dots.push({ x: ps.x, y: ps.y, kind: mobDef(r.info.mob)?.aggro ? 'boss' : 'mob' }); }
    else dots.push({ x: ps.x, y: ps.y, kind: mates.has(r.info.name.toLowerCase()) ? 'party' : 'player' });
  }
  for (const n of npcs) if (n.alpha > 0.5) dots.push({ x: n.x, y: n.y, kind: 'npc' });
  dots.push({ x: pred.state.x / SUB, y: pred.state.y / SUB, kind: 'me', dir: pred.state.dir });
  for (const d of DOORS) if (d.map === map.id) { const [x, y] = doorPoint(d, TS); marks.push({ x, y, kind: d.id.startsWith('line-') ? 'exit' : 'door', label: d.label }); }
  const ws = zoneDef(map.id.split('~')[0])?.wayshrine; if (ws && scene) marks.push({ x: ws[0] * TS + 8, y: ws[1] * TS + 8, kind: 'shrine', label: 'Wayshrine' });
  for (const c of circles) marks.push({ x: c.x, y: c.y, kind: 'battle', label: 'Battle', open: c.can, r: c.r });
  maps.draw(scene, pred.state.x / SUB, pred.state.y / SUB, dots, marks, now);
}

// ---------- what mods can see ----------
function emitInventory(): void {
  const named = (s: { def: string; qty: number; slot: number }) => ({ slot: s.slot, def: s.def, name: itemDef(s.def)?.name ?? s.def, qty: s.qty });
  mods.emit('inventory', { gold: bag.gold, bag: [...bag.slots.values()].map(named), equip: [...charUI.equip.values()].map(named) });
}
function emitSkills(): void { mods.emit('skills', Object.fromEntries(Object.entries(charUI.xp).map(([k, xp]) => [k, { xp, level: levelForXp(xp) }]))); }
let modPosT = 0, modPos = '';
function emitPosition(now: number): void {
  if (!pred || now - modPosT < 250) return; modPosT = now;
  const x = pred.state.x / SUB / TS, y = pred.state.y / SUB / TS, key = x.toFixed(1) + y.toFixed(1) + layer;
  if (key !== modPos) { modPos = key; mods.emit('position', { x, y, zone: zoneName, layer }); }
  if (Math.floor(now / 1000) !== Math.floor((now - 250) / 1000)) mods.emit('players', { inView: world.remotes.size });
}

// ---------- input wiring ----------
input.enabled = () => !dlg && !chat.open && !snap.active && !battle.active && document.getElementById('worldmap')!.hidden;
input.onChatKey = slash => { if (pred && !dlg) { input.clear(); chat.openBox(slash); } };
input.onBagKey = () => { if (pred) { bag.toggle(); audio.play(bag.open ? 'open' : 'close'); } };
input.onPartyKey = () => { if (pred) party.toggleFinder(); };

/** The door you're standing at, if any. */
function nearbyDoor(px: number, py: number): typeof DOORS[number] | null {
  let best: typeof DOORS[number] | null = null, bd = 1e9;
  for (const d of DOORS) { if (d.map !== map.id) continue; const [x, y] = doorPoint(d, TS), dist = Math.hypot(x - px, y - py); if (dist <= DOOR_RANGE_PX && dist < bd) { bd = dist; best = d; } }
  return best;
}
let doorPush = 0, doorWait = 0, wasVz = 0, wasMount = 0, wasFlying = false, rideHintT = 0;
function useDoor(d: typeof DOORS[number]): void { if (performance.now() < doorWait) return; doorWait = performance.now() + 700; conn?.send({ type: 'interact', req: newReq(), target: d.id }); }

/** The forage spot you'd pick from right now, if any. */
function nearbyNode(px: number, py: number): typeof FORAGE[number] | null {
  const now = performance.now(); let best: typeof FORAGE[number] | null = null, bd = 1e9;
  for (const n of FORAGE) {
    if (n.map !== map.id || (nodeReady.get(n.id) ?? 0) > now || !nodeOpen(n)) continue;
    const d = Math.hypot(n.tile[0] * TS + 8 - px, n.tile[1] * TS + 8 - py);
    if (d <= INTERACT_RANGE_PX && d < bd) { bd = d; best = n; }
  }
  return best;
}
chat.onSend = (ch, text, to) => conn?.send({ type: 'chat', ch, text, to });
const myPos = () => pred ? { x: pred.state.x / SUB, y: pred.state.y / SUB } : { x: 0, y: 0 };
input.onAct = () => {
  if (!pred) return;
  const now = performance.now();
  if (dlg) { dlg = advance(dlg, now); return; }
  const p = myPos(), n = nearbyNpc(npcs, p.x, p.y, DIR_NAMES[pred.state.dir]);
  if (n) { dlg = startTalk(n, p.x, p.y, now); input.clear(); return; }
  const door = nearbyDoor(p.x, p.y);
  if (door) { useDoor(door); return; }
  const node = nearbyNode(p.x, p.y);
  if (node) { conn?.send({ type: 'interact', req: newReq(), target: node.id }); nodeReady.set(node.id, now + 1500); }   // dim it until the server answers
};
input.onAttackPress = () => { if (dlg) { dlg = advance(dlg, performance.now()); return true; } return false; };

// ---------- the loop ----------
function simTick(): void {
  if (!pred || !conn) return;
  const before = pred.state.act;
  const own = input.sample(), st0 = pred.state;
  // auto-follow presses the buttons for you (not while fighting or talking); your own keys take back over
  const bits = follow.active && !battle.active && !dlg ? follow.steer(own, { x: st0.x, y: st0.y, mount: !!st0.mount, flying: !!st0.flying }, followTarget(), map, performance.now()) : own;
  const seq = pred.tick(bits);
  // walking into a doorway goes through it: up into buildings, down out of rooms, right down Cottage Lane
  const door = nearbyDoor(pred.state.x / SUB, pred.state.y / SUB);
  const into = door ? IN[pushOf(door)] : 0;
  doorPush = door && (bits & into) ? doorPush + 1 : 0;
  if (door && doorPush >= 6) useDoor(door);   // keeps trying while you push (useDoor waits between tries)
  if (pred.state.act !== before) { if (pred.state.act === Act.Dash) audio.play('dash'); else if (pred.state.act === Act.Attack) audio.play('attack'); }
  const st = pred.state;
  if (st.vz > 0 && wasVz <= 0 && !st.flying) audio.play('jump', undefined, st.mount ? 0.85 : 1);
  if (st.mount !== wasMount) audio.play(st.mount ? 'mount' : 'close');
  if (st.flying && !wasFlying) audio.play('dodge');
  if (!st.mount && (bits & IN.mount) && !map.mounts && performance.now() - rideHintT > 3000) { rideHintT = performance.now(); toast('Mounts are for the open world. Head out past the Town Gate!', 'info'); }
  wasVz = st.vz; wasMount = st.mount; wasFlying = st.flying; input.flying = st.flying;
  inputHist.push(bits); if (inputHist.length > INPUT_REDUNDANCY) inputHist.shift();
  if (seq % INPUT_SEND_EVERY === 0) conn.send({ type: 'input', seq, inputs: inputHist.slice(), ack: world.lastSnapId });
}

let talking = false, skyT = 0;
let last = performance.now(), fps = 0, fpsN = 0, fpsT = last, dbgT = 0, sweepT = 0, worstFrame = 0, worstNow = 0;
function frame(now: number): void {
  const gap = now - last; worstNow = Math.max(worstNow, gap);
  const dt = Math.min(0.1, gap / 1000); last = now;
  world.advance(dt);
  if (now - sweepT > 5000) { sweepT = now; for (const k of [...lastSprite.keys()]) if (k > 0 && k !== me.id && !world.remotes.has(k)) lastSprite.delete(k); for (const k of [...remoteAnim.keys()]) if (!world.remotes.has(k)) remoteAnim.delete(k); for (const [k, b] of bubbles) if (b.until < now) bubbles.delete(k); }
  if (pred && conn?.status === 'online') {
    acc += dt * 1000; let n = 0;
    while (acc >= TICK_MS && n < 8) { simTick(); acc -= TICK_MS; n++; }
    if (n === 8) acc = 0;
  }
  scheduleNpcs(npcs, sky.hour(), dt);
  if (snap.active) { if (!pred) snap.toggle(false); else snap.frame(dt); }
  else if (battle.active) battle.frame(now);
  else draw(acc / TICK_MS, dt, now);
  emitPosition(now);
  drawMinimap(now);
  if (now - skyT > 250) {
    skyT = now;
    audio.setAmbience(pred ? Math.min(1, sky.amount('rain') + sky.amount('storm')) * (room ? 0.35 : 1) : 0, pred ? sky.night() && !room : false);
    const label = sky.label(); if (clockEl.textContent !== label) clockEl.textContent = label;
    clockEl.hidden = !pred;
  }
  if (talking !== !!dlg) { talking = !!dlg; document.body.classList.toggle('talking', talking); }   // chat steps aside for the dialogue box
  fpsN++; if (now - fpsT > 1000) { fps = fpsN * 1000 / (now - fpsT); fpsN = 0; fpsT = now; worstFrame = worstNow; worstNow = 0; }
  if (now - dbgT > 250) { dbgT = now; updateDebug(); }
  requestAnimationFrame(frame);
}
applySettings(settings.s);
requestAnimationFrame(frame);

// ---------- drawing ----------
const lastSprite = new Map<number, HTMLCanvasElement>();
const remoteAnim = new Map<number, number>();   // last animation seen per player, to play their dash and attack sounds
function charFrame(key: number, look: Look | undefined, dir: number, anim: number, t: number, now: number, face: string | null = null): HTMLCanvasElement | null {
  if (!look) return null;
  const d = DIR_NAMES[dir] ?? 'down';
  let mode: Mode = 'i', fi = 0;
  const phase = now + key * 137;
  if (anim === Anim.Attack) { mode = 'a'; fi = attackFrame(t); }
  else if (anim === Anim.Walk) { mode = 'w'; fi = Math.floor(phase / 110) % 6; }   // the townkit walk: six frames
  else if (anim === Anim.Run) { mode = 'w'; fi = Math.floor(phase / 70) % 6; }
  else if (anim === Anim.Dash) { mode = 'w'; fi = Math.floor(phase / 40) % 6; }
  else fi = 0;
  const blink = mode === 'i' && !face && (phase % 3300) < 140;
  const s = sprites.get(look, d, mode, fi, blink, face);
  if (s) { lastSprite.set(key, s); return s; }
  return lastSprite.get(key) ?? null;
}

/** The signpost at the end of the east path: the way home. */
function drawCottageSign(x: number, y: number, g: CanvasRenderingContext2D = ctx): void {
  const ctx = g;
  ctx.fillStyle = C.shadow; ctx.fillRect(x - 5, y - 1, 10, 2);
  ctx.fillStyle = INK; ctx.fillRect(x - 1, y - 22, 3, 22); ctx.fillStyle = '#9a6a3e'; ctx.fillRect(x, y - 21, 1, 20);
  ctx.fillStyle = INK; ctx.fillRect(x - 9, y - 26, 19, 9); ctx.fillStyle = '#e0a87a'; ctx.fillRect(x - 8, y - 25, 17, 7);
  ctx.fillStyle = INK; ctx.fillRect(x + 10, y - 23, 2, 3); ctx.fillStyle = '#e0a87a'; ctx.fillRect(x + 10, y - 22, 1, 1);
  ctx.fillStyle = '#db614f'; ctx.fillRect(x - 6, y - 23, 5, 3); ctx.fillStyle = INK; ctx.fillRect(x - 6, y - 24, 5, 1); ctx.fillRect(x - 4, y - 25, 1, 1);
  ctx.fillStyle = '#7a473f'; ctx.fillRect(x + 1, y - 23, 6, 1); ctx.fillRect(x + 1, y - 21, 4, 1);
}

/** The nearest `n` people, but someone already chosen stays chosen until they drop well past the cut
 *  (`slack` places further). Without this, two people at nearly the same distance trade places every frame
 *  and their name tags and speech bubbles blink on and off. */
const nameKeep = new Set<number>(), calmKeep = new Set<number>();
function stableNearest(items: { id: number; d: number }[], n: number, slack: number, prev: Set<number>): Set<number> {
  items.sort((a, b) => a.d - b.d);
  const out = new Set<number>();
  items.forEach((it, i) => { if (i < n || (i < n + slack && prev.has(it.id))) out.add(it.id); });
  prev.clear(); for (const id of out) prev.add(id);
  return out;
}

/** Someone standing, jumping, riding or flying: shadow on the ground, the mount, then the person on top. */
function drawPerson(px: number, py: number, z: number, m: number, dir: number, moving: boolean, s: HTMLCanvasElement | null, now: number, seed: number): void {
  const x = Math.round(px), y = Math.round(py), lift = Math.round(z), seat = m ? SEAT : 0;
  if (lift || m) drawLiftShadow(ctx, x, y, lift, m ? 11 : 6);
  else { ctx.fillStyle = C.shadow; ctx.fillRect(x - 6, y - 1, 12, 2); ctx.fillRect(x - 4, y + 1, 8, 1); }
  if (m) drawMount(ctx, x, y, lift, dir, m === 2, moving, now, 'back', seed);
  const bob = m === 2 ? Math.round(Math.sin((now / 1000 + seed * 0.37) * 2.4) * 1.5) : m && moving ? -Math.round(Math.abs(Math.sin((now / 1000 + seed * 0.37) * 11))) : 0;
  if (s) ctx.drawImage(s, x - 16, y - 31 - lift - seat + bob); else drawPlaceholder(x, y - lift - seat);
  if (m) drawMount(ctx, x, y, lift, dir, m === 2, moving, now, 'front', seed);
}

function drawPlaceholder(x: number, y: number): void {
  ctx.fillStyle = 'rgba(47,42,38,.45)'; ctx.fillRect(x - 5, y - 20, 10, 18); ctx.fillRect(x - 6, y - 30, 12, 11);
}

function draw(alpha: number, dt: number, now: number): void {
  // rooms are small: zoom in so they fill the screen
  const sc = room ? Math.max(S, Math.floor(Math.min(cv.width / (MW * TS + 40), cv.height / (MH * TS + 70)))) : S, vw = Math.ceil(cv.width / sc), vh = Math.ceil(cv.height / sc);
  sprites.frameStart();
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.imageSmoothingEnabled = false;
  const p = pred ? pred.render(alpha, dt) : { x: map.data.spawn[0] * TS + 8, y: map.data.spawn[1] * TS + 12, z: 0 };
  let camX = Math.max(0, Math.min(MW * TS - vw, p.x - vw / 2)), camY = Math.max(-Math.round(p.z * 0.8), Math.min(MH * TS - vh, p.y - 16 - p.z * 0.6 - vh / 2));   // high up, the camera rises with you
  if (MW * TS < vw) camX = (MW * TS - vw) / 2; if (MH * TS < vh) camY = (MH * TS - vh) / 2;
  const cx = Math.round(camX), cy = Math.round(camY);
  const onScreenC = (x: number, y: number) => x > cx - 60 && x < cx + vw + 60 && y > cy - 60 && y < cy + vh + 60;
  playerScreen = { x: (p.x - cx) * sc, y: (p.y - 16 - cy) * sc };
  ctx.fillStyle = scene ? C.grass : '#2f2a26'; ctx.fillRect(0, 0, cv.width, cv.height);   // flying near the edge: more meadow, not a void
  ctx.setTransform(sc, 0, 0, sc, -cx * sc, -cy * sc); ctx.imageSmoothingEnabled = false;
  if (room) { ctx.drawImage(room.bg, 0, 0); drawWindows(ctx, room, sky.hour(), sky.amount('rain') + sky.amount('storm'), now / 1000); }
  else if (scene) {
    ctx.fillStyle = C.grass; ctx.fillRect(0, 0, MW * TS, MH * TS);
    const gx = Math.max(0, cx), gy = Math.max(0, cy), gw = Math.min(scene.ground.width - gx, vw + 1), gh = Math.min(scene.ground.height - gy, vh + 1);
    if (gw > 0 && gh > 0) ctx.drawImage(scene.ground, gx, gy, gw, gh, gx, gy, gw, gh);
    for (const d of scene.decals) ctx.drawImage(d.img, d.x, d.y);
  }
  // battle circles: a ring on the ground (red: a fight, keep out; green: they'd love some help)
  for (const c of circles) {
    if (!onScreenC(c.x, c.y)) continue;
    const pulse = 0.55 + Math.sin(now / 300) * 0.15, col = c.can ? `rgba(143,224,138,${pulse})` : c.mine ? 'rgba(255,233,168,.6)' : `rgba(236,84,72,${pulse})`;
    ctx.fillStyle = c.can ? 'rgba(143,224,138,.10)' : 'rgba(236,84,72,.08)'; ctx.beginPath(); ctx.ellipse(c.x, c.y, c.r, c.r * 0.8, 0, 0, Math.PI * 2); ctx.fill();
    for (let a = 0; a < Math.PI * 2; a += 0.12) { if (Math.floor(a / 0.12 + now / 160) % 3 === 0) continue; ctx.fillStyle = col; ctx.fillRect(Math.round(c.x + Math.cos(a) * c.r), Math.round(c.y + Math.sin(a) * c.r * 0.8), 2, 2); }
  }
  for (const n of FORAGE) {
    if (n.map !== map.id || (nodeReady.get(n.id) ?? 0) > now || !nodeOpen(n)) continue;
    const x = n.tile[0] * TS + 2, y = n.tile[1] * TS + 2 + (Math.floor(now / 500 + n.tile[0]) % 2);
    ctx.fillStyle = C.shadow; ctx.fillRect(x + 2, n.tile[1] * TS + 13, 8, 2);
    ctx.drawImage(itemIcon(n.item), x, y);
    if (Math.floor(now / 260 + n.tile[1]) % 6 === 0) { ctx.fillStyle = '#ffffff'; ctx.fillRect(x + 10, y - 1, 1, 1); ctx.fillRect(x + 9, y, 3, 1); ctx.fillRect(x + 10, y + 1, 1, 1); }
  }

  // everything that stands on the ground, sorted by feet
  type Item = { y: number; draw: () => void };
  const items: Item[] = [];
  const onScreen = (x: number, y: number) => x > cx - 40 && x < cx + vw + 40 && y > cy - 10 && y < cy + vh + 50;
  if (scene) for (const pr of scene.props) if (onScreen(pr.x, pr.y) || (pr.house && pr.x < cx + vw + 10 && pr.x + pr.house.w * TS > cx - 10 && pr.y > cy - 10 && pr.y - pr.oy < cy + vh)) items.push({ y: pr.y, draw: () => {
    if (pr.shadow) { ctx.fillStyle = C.shadow; ctx.beginPath(); ctx.ellipse(pr.x, pr.y - 1, pr.shadow, 4, 0, 0, Math.PI * 2); ctx.fill(); }
    ctx.drawImage(pr.img, Math.round(pr.x - pr.ox), Math.round(pr.y - pr.oy));
  } });
  if (map.id === 'town') items.push({ y: 14 * TS + 14, draw: () => drawCottageSign(38 * TS + 8, 14 * TS + 14) });
  if (map.id === 'town') for (const d of dummies) items.push({ y: d.y, draw: () => { ctx.fillStyle = C.shadow; ctx.fillRect(d.x - 6, d.y - 1, 12, 2); ctx.drawImage(DUMMY_IMG, d.x - 9, d.y - 29); } });
  for (const n of npcs) if (n.alpha > 0.02 && onScreen(n.x, n.y)) items.push({ y: n.y, draw: () => {
    const x = Math.round(n.x), y = Math.round(n.y);
    ctx.globalAlpha = n.alpha;
    ctx.fillStyle = C.shadow; ctx.fillRect(x - 6, y - 1, 12, 2); ctx.fillRect(x - 4, y + 1, 8, 1);
    const dir = ['down', 'right', 'up', 'left'].indexOf(n.dir);
    const s = charFrame(-1000 - npcs.indexOf(n), n.look, dir < 0 ? 0 : dir, n.walking ? Anim.Walk : Anim.Idle, 0, now, dlg?.npc === n ? dlg.lines[dlg.i]?.[0] === 'n' ? dlg.lines[dlg.i][1] : null : null);
    if (s) ctx.drawImage(s, x - 16, y - 31); else drawPlaceholder(x, y);
    ctx.globalAlpha = 1;
  } });
  const remotePoses: { r: Remote; x: number; y: number }[] = [];
  // calmer crowds: people you chose not to focus on are drawn faded
  const othersMode = settings.s.crowd, faded = new Set<number>();
  if (othersMode !== 'all') {
    const close = othersMode === 'friends' ? new Set([...party.memberNames(), ...party.friendNames()]) : null;
    const list = [...world.remotes.values()].filter(r => r.info?.kind !== 'mob').map(r => ({ r, ps: world.pose(r) })).filter(q => q.ps);
    if (close) { for (const q of list) if (!close.has(q.r.info?.name.toLowerCase() ?? '')) faded.add(q.r.id); }
    else {
      const keep = stableNearest(list.map(q => ({ id: q.r.id, d: (q.ps!.x - p.x) ** 2 + (q.ps!.y - p.y) ** 2 })), 30, 8, calmKeep);
      for (const q of list) if (!keep.has(q.r.id)) faded.add(q.r.id);
    }
  }
  const mobPoses: { r: Remote; x: number; y: number }[] = [];
  for (const r of world.remotes.values()) {
    const ps = world.pose(r); if (!ps || !onScreen(ps.x, ps.y)) continue;
    if (r.info?.kind === 'mob') {
      const fx = mobFx.get(r.id), kind = r.info.mob ?? 'puff';
      if ((r.info.hp ?? 1) <= 0 && !fx?.deadAt) continue;      // already gone when we arrived
      mobPoses.push({ r, x: ps.x, y: ps.y });
      const was = remoteAnim.get(r.id);
      if (was !== ps.anim) { remoteAnim.set(r.id, ps.anim); if (was !== undefined && ps.anim === Anim.Attack) audio.play('attack', { dx: ps.x - p.x, dy: ps.y - p.y }, 1.35); }
      items.push({ y: ps.y, draw: () => drawMob(ctx, { kind, x: ps.x, y: ps.y, dir: ps.dir, anim: (r.info?.hp ?? 1) <= 0 ? Anim.Idle : ps.anim, t: ps.t, hp: r.info?.hp ?? 1, max: r.info?.max ?? 1, hurtAt: fx?.hurtAt ?? 0, deadAt: fx?.deadAt ?? 0, id: r.id }, now) });
      continue;
    }
    const lift = ps.z + (ps.m ? SEAT : 0);
    remotePoses.push({ r, x: ps.x, y: ps.y - lift });
    const was = remoteAnim.get(r.id);
    if (was !== ps.anim) {
      remoteAnim.set(r.id, ps.anim);
      if (was !== undefined && (ps.anim === Anim.Dash || ps.anim === Anim.Attack)) audio.play(ps.anim === Anim.Dash ? 'dash' : 'attack', { dx: ps.x - p.x, dy: ps.y - p.y }, 0.92 + (r.id % 7) * 0.025);
    }
    items.push({ y: ps.y + ps.z, draw: () => {
      const fade = faded.has(r.id);
      if (fade) ctx.globalAlpha = 0.28;
      drawPerson(ps.x, ps.y, ps.z, ps.m, ps.dir, ps.anim === Anim.Walk || ps.anim === Anim.Run, charFrame(r.id, r.info?.look, ps.dir, ps.m ? Anim.Idle : ps.anim, ps.t, now), now, r.id);
      if (fade) ctx.globalAlpha = 1;
    } });
  }
  if (pred && me.look) {
    const st = pred.state, face = dlg && dlg.lines[dlg.i]?.[0] === 'p' ? dlg.lines[dlg.i][1] : null;
    items.push({ y: p.y + p.z, draw: () => {
      const s = charFrame(me.id, me.look!, st.dir, st.mount ? Anim.Idle : animOf(st), st.act === Act.Attack ? ATTACK_TICKS - st.actT : 0, now, face);
      drawPerson(p.x, p.y, p.z, st.mount ? (st.flying ? 2 : 1) : 0, st.dir, st.moving, s, now, me.id);
    } });
  }
  items.sort((a, b) => a.y - b.y);
  for (const it of items) it.draw();
  if (scene) scene.ambient(ctx, now / 1000, cx, cy, vw, vh);
  // day and night, weather: lights from lamps, windows, moonflowers and every person (lanterns shine further)
  if (sky.darkness() > 0.02 || sky.weather() !== 'clear' || sky.amount('rain') > 0) {
    const lights: Light[] = room ? room.lights.slice() : (scene?.lamps ?? []).slice();
    const personal = (x: number, y: number, look: Look | undefined) => lights.push({ x, y: y - 14, r: look?.wear?.includes('lantern') ? 62 : 24, warm: true });
    if (pred) personal(p.x, p.y, me.look ?? undefined);
    for (const q of remotePoses) personal(q.x, q.y, q.r.info?.look);
    for (const n of npcs) if (n.alpha > 0.3 && onScreen(n.x, n.y)) personal(n.x, n.y, n.look);
    for (const fn of FORAGE) if (fn.when === 'night' && fn.map === map.id && nodeOpen(fn) && (nodeReady.get(fn.id) ?? 0) <= now) lights.push({ x: fn.tile[0] * TS + 8, y: fn.tile[1] * TS + 8, r: 18 });
    sky.draw(ctx, cx, cy, vw, vh, lights, dt, now / 1000, !!room);
  }

  // names, bubbles, the talk hint
  // in a crowd, only the nearest players (and your party) get name tags and speech bubbles, so it stays readable
  const nameMode = settings.s.names;
  const shown = faded.size ? remotePoses.filter(q => !faded.has(q.r.id)) : remotePoses;
  let named = nameMode === 'off' ? [] : shown;
  if (nameMode === 'party') { const mates = party.memberNames(); named = shown.filter(q => mates.has(q.r.info?.name.toLowerCase() ?? '')); }
  else if (nameMode === 'crowd' && shown.length > CROWD_NAMES) {
    const mates = party.memberNames();
    const keep = stableNearest(shown.map(q => ({ id: q.r.id, d: (q.x - p.x) ** 2 + (q.y - p.y) ** 2 })), CROWD_NAMES, 6, nameKeep);
    named = shown.filter(q => keep.has(q.r.id) || mates.has(q.r.info?.name.toLowerCase() ?? ''));
  }
  for (const { r, x, y } of named) if (r.info) drawName(ctx, x, y, r.info.name, factionById(r.info.faction)?.color ?? '#fff', false);
  const myLift = pred ? p.z + (pred.state.mount ? SEAT : 0) : 0;
  for (const c of circles) if (c.can && !c.mine && onScreenC(c.x, c.y)) drawName(ctx, c.x, c.y - c.r * 0.8 + 24, 'Help wanted! Walk in', '#8fe08a', true);
  if (pred) drawName(ctx, p.x, p.y - myLift, me.name, factionById(me.faction)?.color ?? '#fff', true);
  // creatures: a health bar once they're hurt, and a name and level when you're close
  for (const { r, x, y } of mobPoses) {
    const i = r.info!, def = mobDef(i.mob), top = y - mobTop(i.mob ?? 'puff'), hp = i.hp ?? 1, max = i.max ?? 1, fx = mobFx.get(r.id);
    if (fx?.deadAt) continue;
    const close = (x - p.x) ** 2 + (y - p.y) ** 2 < 56 * 56;
    if (hp < max) drawHpBar(ctx, x, top - 4, hp, max);
    if (close && def) drawName(ctx, x, top + (hp < max ? -6 : 0) + 31, `Lv${def.level} ${def.name}`, def.aggro ? '#f5a08a' : '#f3eee3', false);
  }
  for (let i = floaters.length - 1; i >= 0; i--) {
    const f = floaters[i], k = (now - f.t0) / 900;
    if (k >= 1) { floaters.splice(i, 1); continue; }
    const fy = Math.round(f.y - k * 14 - (f.big ? Math.sin(Math.min(1, k * 4) * Math.PI) * 3 : 0));
    ctx.font = `${f.big ? 10 : 8}px ${FONT}`; ctx.textAlign = 'center'; ctx.globalAlpha = Math.min(1, (1 - k) * 2.5);
    ctx.fillStyle = INK; for (const [ox, oy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) ctx.fillText(f.text, Math.round(f.x) + ox, fy + oy);
    ctx.fillStyle = f.color; ctx.fillText(f.text, Math.round(f.x), fy);
    ctx.globalAlpha = 1; ctx.textAlign = 'left';
  }
  if (!dlg && settings.s.bubbles) {
    for (const { r, x, y } of (nameMode === 'crowd' ? named : shown.slice(0, 40))) { const b = bubbles.get(r.id); if (b && b.until > now) drawBubble(ctx, x, y - 8, 'say', b.text, cy, (now - b.from) / (b.until - b.from)); }
    const mb = bubbles.get(me.id); if (pred && mb && mb.until > now) drawBubble(ctx, p.x, p.y - 8 - myLift, 'say', mb.text, cy, (now - mb.from) / (mb.until - mb.from));
    for (const n of npcs) if (n.bubble && n.alpha >= 1 && onScreen(n.x, n.y)) drawBubble(ctx, n.x, n.y, n.bubble.kind, n.bubble.content, cy, 1 - (n.bubble.until - now) / (n.bubble.kind === 'say' ? 2800 : 3200));
    if (pred) {
      const n = nearbyNpc(npcs, p.x, p.y, DIR_NAMES[pred.state.dir]);
      const hint = (hx: number, hy: number) => { hy += Math.floor(now / 300) % 2; ctx.fillStyle = INK; ctx.fillRect(hx - 1, hy, 3, 5); ctx.fillRect(hx - 1, hy + 6, 3, 2); ctx.fillStyle = '#f9c776'; ctx.fillRect(hx, hy + 1, 1, 3); ctx.fillRect(hx, hy + 6, 1, 1); };
      const door = nearbyDoor(p.x, p.y);
      if (n) hint(Math.round(n.x), Math.round(n.y) - 40);
      else if (door) { const [dx, dy] = doorPoint(door, TS), push = pushOf(door); drawName(ctx, Math.min(MW * TS - 30, Math.max(30, dx)), Math.max(cy + 40, dy + (push === 'up' ? 6 : -8)), door.label, '#f9c776', true); }
      else { const fn = nearbyNode(p.x, p.y); if (fn) hint(fn.tile[0] * TS + 8, fn.tile[1] * TS - 8); }
    }
  }
  updateNpcs(npcs, now, p.x, p.y, dlg?.npc ?? null);
  walkNpcs(npcs, dt, now, map, p.x, p.y, dlg?.npc ?? null);

  // screen space
  ctx.setTransform(sc, 0, 0, sc, 0, 0);
  if (dlg && me.look) {
    const line = dlg.lines[dlg.i];
    const shownBefore = dlg.shown;
    if (!dlg.skip) dlg.shown = Math.min(line[2].length, Math.floor((now - dlg.t0) / 28));
    if (Math.floor(dlg.shown / 3) > Math.floor(shownBefore / 3) && line[2][dlg.shown - 1] !== ' ') audio.play('blip', undefined, line[0] === 'p' ? 1.25 : 0.85 + (npcs.indexOf(dlg.npc) % 4) * 0.08);
    const mine = line[0] === 'p';
    if (pred) dlg.npc.dir = dirToward(p.x - dlg.npc.x, p.y - dlg.npc.y);
    drawDialogue(ctx, vw, vh - Math.ceil(padInset * S / sc), {
      name: mine ? me.name : dlg.npc.name, right: mine,
      portrait: sprites.portrait(mine ? me.look : dlg.npc.look, line[1]),
      tagColor: mine ? (factionById(me.faction)?.color ?? '#fff') : '#e6dcc4',
      text: line[2].slice(0, dlg.shown), done: dlg.shown >= line[2].length,
    }, now);
  }
}

function updateDebug(): void {
  if (!conn || !pred) { debug.set(null); return; }
  conn.tickStats();
  debug.set([
    `transport  ${conn.kind} · ${conn.codec.name}`,
    conn.transportNote ? `           ${conn.transportNote}` : '',
    `ping       ${conn.rtt.toFixed(0)} ms  ±${conn.jitter.toFixed(0)}`,
    `snapshots  ${world.snapsPerSec.toFixed(0)}/s  delay ${(world.delayTicks * TICK_MS).toFixed(0)} ms`,
    `corrected  ${pred.corrections} (snapped ${pred.snaps}, last ${pred.lastError.toFixed(1)} px)`,
    `unacked    ${pred.unacked} inputs`,
    `players    ${world.remotes.size + 1} in view · layer ${layer}`,
    `bandwidth  ↓${conn.kbIn.toFixed(1)} ↑${conn.kbOut.toFixed(1)} KB/s`,
    `fps        ${fps.toFixed(0)}  sprites ${sprites.built}`,
  ].filter(Boolean));
}


// Read-only hook for automated browser tests and console debugging.
(window as unknown as { __tt: unknown }).__tt = {
  get me() { return me; }, get pred() { return pred; }, world, get conn() { return conn; }, get npcs() { return npcs; },
  get dlg() { return dlg; }, sprites, audio, settings, renderPreview, get zoneId() { return map.id; },
  /** Open a conversation with a townsperson (for UI checks on different screens). */
  talk(i = 0) { const p = myPos(); dlg = startTalk(npcs[i], p.x, p.y, performance.now()); },
};
